<script>
export default {
  data () {
    return {
      env: {
        google_omniauth_url: process.env.VUE_APP_GOOGLE_OMNIAUTH_URL,
        google_client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID
      }
    }
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    signed_to_newsletter: {
      type: Boolean,
      default: false
    },
    agreed_to_terms_and_conditions: {
      type: Boolean,
      default: true
    },
    intent: {
      type: String,
      default: 'login',
      validator: (value) => ['login', 'signup'].includes(value)
    }
  },
  computed: {
    buttonLabel () {
      return this.intent === 'signup' ? this.$t('auth.login.google_signup_btn') : this.$t('auth.login.google_login_btn')
    }
  },
  mounted () {
    if (window.gsiReady) {
      this.initGoogleButton()
    } else {
      window.addEventListener('gsi-ready', this.initGoogleButton, { once: true })
    }
  },
  beforeDestroy () {
    window.removeEventListener('gsi-ready', this.initGoogleButton)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  },
  methods: {
    initGoogleButton () {
      if (!window.google || !window.google.accounts || !window.google.accounts.id) {
        return
      }

      window.google.accounts.id.initialize({
        client_id: this.env.google_client_id,
        callback: this.handleGoogleResponse,
        use_fedcm_for_prompt: true
      })

      if (this.intent === 'login') {
        window.google.accounts.id.prompt()
      }

      this.$nextTick(() => this.renderGoogleButton())

      this.resizeObserver = new ResizeObserver(() => this.renderGoogleButton())
      this.resizeObserver.observe(this.$el)

      // Safari allows only one Google sign-in popup per page load — a second attempt is
      // silently blocked by the browser itself. Reloading whenever the user returns to the
      // tab after starting (but not completing) an attempt makes the next tap a fresh
      // page's first attempt again, which Safari does allow.
      const container = document.getElementById('google-signin-button')
      if (container) {
        container.addEventListener('pointerdown', this.markAttemptStarted)
      }
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
    },
    markAttemptStarted () {
      this.attemptStartedAt = Date.now()
    },
    handleVisibilityChange () {
      if (document.visibilityState !== 'visible' || !this.attemptStartedAt) {
        return
      }

      const elapsed = Date.now() - this.attemptStartedAt
      this.attemptStartedAt = null

      // Ignore near-instant visibility flips — not a real trip to the Google popup.
      if (elapsed < 300) {
        return
      }

      // Give a genuinely successful login (handleGoogleResponse, fired via postMessage as
      // the popup closes) a brief window to clear attemptStartedAt itself before we reload.
      setTimeout(() => {
        if (!this.loginSucceeded) {
          window.location.reload()
        }
      }, 150)
    },
    renderGoogleButton () {
      const wrapper = this.$el.querySelector('.google-button-wrapper') || this.$el
      const width = Math.round(wrapper.getBoundingClientRect().width)
      const container = document.getElementById('google-signin-button')

      if (!width || !container) {
        return
      }

      container.innerHTML = ''
      window.google.accounts.id.renderButton(
        container,
        {
          theme: 'outline',
          size: 'large',
          text: this.intent === 'signup' ? 'signup_with' : 'signin_with',
          shape: 'rectangular',
          width: Math.min(width, 400)
        }
      )
    },
    async handleGoogleResponse (response) {
      this.loginSucceeded = true
      this.$Progress.start()

      try {
        await this.$auth.login({
          url: this.env.google_omniauth_url,
          method: 'POST',
          data: {
            credential: response.credential,
            signed_to_newsletter: this.signed_to_newsletter,
            agreed_to_terms_and_conditions: this.agreed_to_terms_and_conditions,
            intent: this.intent
          },
          rememberMe: true,
          fetchUser: true,
          redirect: false
        })

        if (this.$auth.check()) {
          this.$store.dispatch('profile/account/loadCurrentUser')
        }

        // Profile completion is currently not required. Kept for possible later use.
        // if (!this.$auth.user().profile_completed) {
        //   this.$router.push({ name: 'profile.account' })
        //
        //   this.$notify({
        //     type: 'warn',
        //     text: this.$t('profile.account.update_your_account_data')
        //   })
        // } else if (this.$auth.user().subscriptions.length > 0) {
        if (this.$auth.user().subscriptions.length > 0) {
          this.$router.push({ name: `${this.$auth.user().subscriptions[0].markets[0].slug}.instruments` })
        } else {
          this.$router.push({ name: 'default' })
        }
      } catch (error) {
        this.$notify({
          type: 'error',
          text: this.devise().formatResponse(error.response.data) || this.$t('common.unknown_error')
        })
      } finally {
        this.$Progress.finish()
      }
    }
  }
}
</script>

<template>
  <div :class="{ disabled }" class="google-login d-flex align-items-center justify-content-center w-100">
    <div class="google-button-wrapper position-relative w-100">
      <div class="google-custom-button d-flex align-items-center justify-content-center w-100 bg-white rounded-0">
        <svg class="google-icon mr-2" viewBox="0 0 48 48" width="18" height="18">
          <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12
            c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24
            c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
          <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039
            l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
          <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36
            c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
          <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571
            c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24
            C44,22.659,43.862,21.35,43.611,20.083z"/>
        </svg>
        <span>{{ buttonLabel }}</span>
      </div>
      <div id="google-signin-button" class="google-real-button position-absolute"></div>
      <div v-if="disabled" class="google-disabled-overlay position-absolute"></div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.google-login.disabled {
  opacity: 0.5;
}

.google-disabled-overlay {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.google-custom-button {
  height: 40px;
  border: 1px solid #dadce0;
  color: #3c4043;
  font-size: 14px;
  font-weight: 500;
  pointer-events: none;
}

.google-real-button {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  opacity: 0;
}
</style>
